<template>
  <div>
    <dialog-v2
      :show="show"
      :title="title"
      @close="$emit('close')"
      @save="save">
      <template slot="content">
        <v-layout row wrap>
          <v-flex xs12>
            <v-text-field
              v-model="form.name"
              v-validate="'required|max:45'"
              data-vv-name="name"
              data-vv-scope="workflowNotification"
              data-vv-as="nombre"
              label="Nombre"
              color="black"
              :error-messages="errors.collect('workflowNotification.name')"/>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex xs12>
            <v-text-field
              v-model="form.description"
              v-validate="'max:1000'"
              data-vv-name="description"
              data-vv-scope="workflowNotification"
              data-vv-as="descripción"
              label="Descripción"
              color="black"
              :error-messages="errors.collect('workflowNotification.description')"/>
          </v-flex>
        </v-layout>
        <v-tabs
          v-model="selectedTab"
          grow>
          <v-tabs-slider color="k-orange-linear"></v-tabs-slider>
          <v-tab>
            Límite mensual
          </v-tab>
          <v-tab>
            Límite diario
          </v-tab>
          <v-tab>
            Configuración diaria
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="selectedTab">
          <v-tab-item class="pt-3">
            <v-subheader class="pl-0 my-2">
              Limites por canal
            </v-subheader>
            <v-layout
              v-for="(channel, i) in workflowChannels"
              :key="`channel-${channel.name}`"
              :mt-4="i"
              row
              wrap
              justify-space-between
              align-center>
              <v-flex xs1 text-xs-center>
                <v-icon class="k-orange-linear-text">
                  {{ channel.icon }}
                </v-icon>
              </v-flex>
              <v-flex md3>
                <v-select
                  v-model="monthlyConfig[`t_${channel.name}`]"
                  :items="limitList"
                  :hide-details="true"
                  :disabled="!canEditWhatsappWorkflow && channel.name === 'whatsapp'"
                  label="Total"
                  color="black"
                  autocomplete>
                </v-select>
              </v-flex>
              <v-flex md3>
                <v-select
                  v-model="monthlyConfig[`r_${channel.name}`]"
                  :items="limitList"
                  :hide-details="true"
                  :disabled="!canEditWhatsappWorkflow && channel.name === 'whatsapp'"
                  label="Contactado"
                  color="black"
                  autocomplete>
                </v-select>
              </v-flex>
              <v-flex md3>
                <v-select
                  v-model="monthlyConfig[`c_${channel.name}`]"
                  :items="limitList"
                  :hide-details="true"
                  :disabled="!canEditWhatsappWorkflow && channel.name === 'whatsapp'"
                  label="Gestión efectiva"
                  color="black"
                  autocomplete>
                </v-select>
              </v-flex>
            </v-layout>
          </v-tab-item>
          <v-tab-item class="pt-3">
            <div class="caption grey--text mb-3">
              La etapa solo cuenta gestiones que ya salieron hoy (para contabilizarlas
              correctamente si hay una recarga del día). Las gestiones nuevas que se
              programen en esta corrida se suman al cupo diario aparte, sin filtrar por
              etapa.
            </div>
            <v-layout
              v-for="(channel, i) in workflowChannels"
              :key="`daily-limit-channel-${channel.name}`"
              :mt-4="i"
              row
              wrap
              justify-space-between
              align-center>
              <v-flex xs1 text-xs-center>
                <v-icon class="k-orange-linear-text">
                  {{ channel.icon }}
                </v-icon>
              </v-flex>
              <v-flex md5>
                <v-select
                  v-model="dailyLimitsConfig[channel.name].cs_journey_from"
                  :items="csJourneys"
                  item-value="id"
                  item-text="name"
                  :hide-details="true"
                  :disabled="!canEditWhatsappWorkflow && channel.name === 'whatsapp'"
                  label="Etapa desde la cual cuenta"
                  color="black"
                  autocomplete>
                </v-select>
              </v-flex>
              <v-flex md5>
                <v-text-field
                  v-model.number="dailyLimitsConfig[channel.name].max"
                  type="number"
                  min="0"
                  :hide-details="true"
                  :disabled="!canEditWhatsappWorkflow && channel.name === 'whatsapp'"
                  label="Máximo diario"
                  color="black">
                </v-text-field>
              </v-flex>
            </v-layout>
          </v-tab-item>
          <v-tab-item class="pt-3">
            <v-layout row wrap justify-space-between align-center mt-3>
              <v-flex>
                <v-subheader class="pl-0">
                  Configuraciones
                </v-subheader>
              </v-flex>
              <v-flex md3 text-xs-right>
                <v-btn small depressed round class="k-orange-linear mr-0" @click.stop="dailyDialog = true">
                  <v-icon small class="mr-1">
                    fas fa-plus
                  </v-icon>
                  Agregar
                </v-btn>
              </v-flex>
            </v-layout>
            <template v-if="dailyConfig.length">
              <v-layout
                v-for="(item, i) in dailyConfig" :key="`config-${i}`"
                row
                wrap
                justify-center
                align-center
                py-1>
                <v-flex md9>
                  <template v-for="(day, b) in item.week_days">
                    <span class="text-capitalize" :key="`day-${day}`">
                      {{ formatDate(day, 'd', 'dddd') }}
                    </span>
                    <template v-if="b !== item.week_days.length - 1">
                      -
                    </template>
                  </template>
                </v-flex>
                <v-flex md3 class="text-xs-right">
                  <v-btn flat icon small @click="editDailyConfig(item, i)">
                    <v-icon small>
                      fas fa-pencil-alt
                    </v-icon>
                  </v-btn>
                  <v-btn flat icon small @click="deleteDailyConfig(item, i)">
                    <v-icon small>
                      fas fa-trash-alt
                    </v-icon>
                  </v-btn>
                </v-flex>
              </v-layout>
            </template>
            <v-layout row wrap v-else py-2>
              <v-flex offset-md3 xs1 text-xs-center>
                <v-icon small>
                  fas fa-info-circle
                </v-icon>
              </v-flex>
              <v-flex class="ml-3">
                No hay nada que mostrar
              </v-flex>
            </v-layout>
          </v-tab-item>
        </v-tabs-items>
      </template>
    </dialog-v2>
    <daily-dialog
      :show="dailyDialog"
      :data="dailyConfigData"
      :is-whatsapp-outbound-active="isWhatsappOutboundActive"
      :daily-config-management-channels="dailyConfigManagementChannels"
      :daily-config-empty-form="dailyConfigEmptyForm"
      @save="saveDailyConfig"
      @close="dailyDialog = false" />
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { setTimeout } from 'timers';
import EventBus from '@/event-bus';
import channelsMixin from '@/mixins/channelsV2';
import formatDateMixin from '@/mixins/formatDate';
import permissionsMixin from '@/mixins/permissions';
import DialogV2 from '@/components/global/DialogV2';
import DailyDialog from '@/components/CollectWorkflows/DailyDialog';

export default {
  inject: ['$validator'],
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    workflow: {
      type: Object,
      required: false,
    },
    isWhatsappOutboundActive: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DialogV2,
    DailyDialog,
  },
  mixins: [
    formatDateMixin,
    channelsMixin,
    permissionsMixin,
  ],
  created() {
    moment.locale('es');
    this.dailyLimitsConfig = {
      ...this.emptyDailyLimitsConfig,
    };
  },
  data() {
    const emptyForm = {
      name: '',
      description: '',
    };
    const limitList = [
      {
        text: 'Ilimitado',
        value: 0,
      },
    ];
    for (let i = 1; i <= 100; i += 1) {
      limitList.push({
        text: i,
        value: i,
      });
    }
    const csJourneys = [
      {
        id: 1,
        name: 'Gestionando',
      },
      {
        id: 2,
        name: 'Barrido general',
      },
      {
        id: 3,
        name: 'Barrido exitoso',
      },
      {
        id: 4,
        name: 'Conexión',
      },
      {
        id: 5,
        name: 'Contacto',
      },
      {
        id: 6,
        name: 'Contacto directo',
      },
      {
        id: 7,
        name: 'Compromiso',
      },
    ];
    return {
      dailyDialog: false,
      selectedTab: '1',
      emptyForm,
      form: {
        ...emptyForm,
      },
      limitList,
      csJourneys,
      dailyConfigSelected: null,
      monthlyConfig: {},
      dailyLimitsConfig: {},
      dailyConfig: [],
    };
  },
  computed: {
    title() {
      let title;
      if (Object.keys(this.workflow).length) {
        title = `Editar flujo ${this.workflow.name.toLowerCase()}`;
      } else {
        title = 'Crear flujo';
      }
      return title;
    },
    dailyConfigData() {
      let data = {};
      if (this.dailyConfigSelected !== null) {
        data = this.dailyConfig[this.dailyConfigSelected];
      }
      return data;
    },
    canEditWhatsappWorkflow() {
      return this.checkPermissions([
        'edit_whatsapp_workflow',
      ]);
    },
    workflowChannels() {
      const includedChannels = ['sms', 'ivr', 'bot', 'human', 'visit', 'email'];
      if (this.isWhatsappOutboundActive) {
        includedChannels.push('whatsapp');
      }
      return this.channels.filter((channel) => includedChannels.includes(channel.name));
    },
    emptyMonthlyConfig() {
      let emptyConfig = {};
      for (let i = 0; i < this.workflowChannels.length; i += 1) {
        emptyConfig = {
          ...emptyConfig,
          [`t_${this.workflowChannels[i].name}`]: 0,
          [`r_${this.workflowChannels[i].name}`]: 0,
          [`c_${this.workflowChannels[i].name}`]: 0,
        };
      }
      return emptyConfig;
    },
    emptyDailyLimitsConfig() {
      let emptyConfig = {};
      for (let i = 0; i < this.workflowChannels.length; i += 1) {
        emptyConfig = {
          ...emptyConfig,
          [this.workflowChannels[i].name]: {
            cs_journey_from: null,
            max: 0,
          },
        };
      }
      return emptyConfig;
    },
    dailyConfigManagementChannels() {
      const includedChannels = ['human', 'bot', 'email', 'sms', 'ivr'];
      if (this.isWhatsappOutboundActive) {
        includedChannels.push('whatsapp');
      }
      return this.channels.filter(item => includedChannels.includes(item.name));
    },
    dailyConfigEmptyForm() {
      return this.dailyConfigManagementChannels.reduce((acc, cur) => {
        return {
          ...acc,
          [cur.name]: {
            order: null,
            purse_limit: 0,
            management_limit: null,
            ...(cur.name === 'human') && {
              agent_limit: null,
              multiple_management: {
                enabled: false,
                failed_attempts: 0,
              },
            },
            min_hour: null,
            max_hour: null,
            min_due_balance: null,
            assignment_order: {
              amount: null,
              delinquency: null,
            },
          },
        };
      }, { week_days: [] });
    },
  },
  methods: {
    checkChannelsConfig(){
      const currentConfig = JSON.parse(this.workflow.config);
      if (!currentConfig.daily_limits) {
        currentConfig.daily_limits = {};
      }
      ['human', 'bot', 'email', 'sms', 'ivr'].forEach(channel => {
        if(currentConfig.monthly[`c_${channel}`] === undefined) {
          const dayliConfigCount = currentConfig.daily.length

          currentConfig.monthly[`t_${channel}`] = 0;
          currentConfig.monthly[`r_${channel}`] = 0;
          currentConfig.monthly[`c_${channel}`] = 0;

          if(dayliConfigCount > 0) {
            const channelData = {
              "order": null,
              "purse_limit": null,
              "management_limit": null,
              "min_hour": null,
              "max_hour": null,
              "min_due_balance": null,
              "assignment_order": {
                "amount": null,
                "delinquency": null
              }
            }
            for (let index = 0; index < dayliConfigCount; index++) {
              currentConfig.daily[index][channel] = channelData;
            }
          }
          if (currentConfig.daily_limits[channel] === undefined) {
            currentConfig.daily_limits[channel] = {
              cs_journey_from: null,
              max: 0,
            };
          }
          this.workflow.config = JSON.stringify(currentConfig);
        }
      });
    },
    clearErrors() {
      Object.keys(this.form).forEach((key) => {
        this.$validator.errors.remove(key, 'workflowNotification');
      });
    },
    saveDailyConfig(form) {
      let checkDays;
      if (this.dailyConfigSelected !== null) {
        checkDays = this.dailyConfig.filter(
          (item, i) => i !== this.dailyConfigSelected
            && item.week_days.some(item2 => form.week_days.includes(item2)),
        ).length;
        if (!checkDays) {
          this.$set(this.dailyConfig, this.dailyConfigSelected, form);
          this.dailyDialog = false;
        } else {
          EventBus.$emit('modal', 'Ya existe una configuracion para estos días de la semana');
        }
      } else {
        checkDays = this.dailyConfig.filter(
          item => item.week_days.some(item2 => form.week_days.includes(item2)),
        ).length;
        if (!checkDays) {
          this.dailyConfig.push(form);
          this.dailyDialog = false;
        } else {
          EventBus.$emit('modal', 'Ya existe una configuracion para estos días de la semana');
        }
      }
    },
    editDailyConfig(item, i) {
      this.dailyConfigSelected = i;
      this.dailyDialog = true;
    },
    deleteDailyConfig(item, index) {
      const dailyConfig = [
        ...this.dailyConfig,
      ];
      dailyConfig.splice(index, 1);
      this.dailyConfig = dailyConfig;
    },
    save() {
      this.$validator.validateAll('workflowNotification').then((validation) => {
        if (validation) {
          const checkDays = this.dailyConfig.reduce((before, current) => [
            ...before,
            ...current.week_days,
          ], []);
          if (checkDays.length === 7) {
            const dailyLimits = this.workflowChannels.reduce((acc, channel) => {
              const channelLimit = this.dailyLimitsConfig[channel.name] || {};
              if (channelLimit.max) {
                acc[channel.name] = {
                  cs_journey_from: channelLimit.cs_journey_from,
                  max: channelLimit.max,
                };
              }
              return acc;
            }, {});
            const data = {
              ...this.form,
              config: JSON.stringify({
                monthly: this.monthlyConfig,
                daily: this.dailyConfig,
                daily_limits: dailyLimits,
              }),
            };
            if (Object.keys(this.workflow).length) {
              this.axios.patch(`workflows/${this.workflow.id}`, data)
                .then((response) => {
                  this.$emit('updated', response.data);
                  this.$emit('close');
                });
            } else {
              this.axios.post('workflows', data)
                .then((response) => {
                  this.$emit('created', response.data);
                  this.$emit('close');
                })
                .catch(() => {});
            }
          } else {
            EventBus.$emit('modal', 'Debe haber una configuración para todos los días de la semana.');
          }
        }
      });
    },
  },
  watch: {
    show() {
      if (this.show) {
        this.selectedTab = '0';
        this.monthlyConfig = {
          ...this.emptyMonthlyConfig,
        };
        this.dailyLimitsConfig = {
          ...this.emptyDailyLimitsConfig,
        };
        if (Object.keys(this.workflow).length) {
          this.checkChannelsConfig();
          this.form = _.pick({
            ...this.emptyForm,
            ...this.workflow,
          }, Object.keys(this.emptyForm));
          const config = JSON.parse(this.workflow.config || '{}');
          this.dailyConfig = config.daily || [];
          this.monthlyConfig = {
            ...this.monthlyConfig,
            ...(config.monthly || {}),
          };
          this.dailyLimitsConfig = {
            ...this.dailyLimitsConfig,
            ...(config.daily_limits || {}),
          };
          this.clearErrors();
        }
      } else {
        setTimeout(() => {
          this.form = {
            ...this.emptyForm,
          };
          this.dailyConfig = [];
          this.monthlyConfig = {
            ...this.emptyMonthlyConfig,
          };
          this.dailyLimitsConfig = {
            ...this.emptyDailyLimitsConfig,
          };
          this.clearErrors();
        }, 200);
      }
    },
    dailyDialog() {
      if (!this.dailyDialog) {
        this.dailyConfigSelected = null;
      }
    },
  },
};
</script>


// WEBPACK FOOTER //
// src/components/V2CollectWorkflows/WorkflowDialog.vue